import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { formatDocumentTitle, getRouteTitle } from "../utils/pageTitle";

/*
 * Keeps the document's identity -- title, canonical link, og:url -- in step
 * with the current route.
 *
 * Prerendering stamps all three into every page, correct for the URL that was
 * requested. Client-side navigation then changed the URL and left them behind,
 * so one in-app click and the tab title, the canonical and the share URL all
 * still described the page the visitor had arrived on.
 *
 * Static route titles come from the site search index. Dynamic routes
 * (/people/:id, /battles/:id) are skipped here and set their own via
 * useDocumentTitle, since only the page knows the record's name. The canonical
 * and og:url need no such help: the pathname is the whole answer.
 */

const RECORD_PREFIXES = ["/people/", "/battles/"];

/*
 * The origin the build stamped into the prerendered canonical rather than
 * window.location.origin: viewed on a preview or *.vercel.app host, the
 * canonical still has to name the primary domain instead of declaring the
 * preview to be the real site.
 */
function siteOrigin() {
  try {
    return new URL(
      document.querySelector('link[rel="canonical"]').getAttribute("href")
    ).origin;
  } catch {
    return window.location.origin;
  }
}

/*
 * A path the app actually serves canonicalises to itself. Anything else is a
 * soft 404 -- the SPA fallback answers it with the home page -- and pointing
 * those at the root consolidates junk URLs instead of inviting a crawler to
 * index every typo as its own page.
 */
function canonicalPathFor(pathname) {
  if (pathname === "/" || getRouteTitle(pathname) !== null) return pathname;

  const isRecord = RECORD_PREFIXES.some(
    (prefix) => pathname.startsWith(prefix) && pathname.length > prefix.length
  );

  return isRecord ? pathname : "/";
}

function RouteMetadata() {
  const { pathname } = useLocation();

  useEffect(() => {
    const routeTitle = getRouteTitle(pathname);

    /* null is a dynamic route that titles itself; leave the title to it. */
    if (pathname === "/" || routeTitle !== null) {
      document.title = formatDocumentTitle(routeTitle);
    }

    const url = `${siteOrigin()}${canonicalPathFor(pathname)}`;

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", url);

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute("content", url);
  }, [pathname]);

  return null;
}

export default RouteMetadata;
